import { isSignedOutMarkerActive } from '../utils/authFlow';
import { LEGACY_SUPABASE_AUTH_STORAGE_KEY } from './authStorageKeys';

export const VERIFIED_PROFILE_EVENT = 'verified-profile-updated';
export const VERIFIED_PROFILE_STORAGE_PREFIX = 'verifiedProfile-';

const getActiveAuthStorageKey = () => (
  typeof window !== 'undefined'
    ? window.__AP_SUPABASE_AUTH_STORAGE_KEY__ || LEGACY_SUPABASE_AUTH_STORAGE_KEY
    : LEGACY_SUPABASE_AUTH_STORAGE_KEY
);

const extractSessionUser = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.currentSession?.user) return payload.currentSession.user;
  if (payload.session?.user) return payload.session.user;
  if (payload.user && payload.access_token) return payload.user;
  if (payload.data?.session?.user) return payload.data.session.user;
  return null;
};

const readAuthRecord = (storage) => {
  if (typeof storage === 'undefined') return null;

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || key !== getActiveAuthStorageKey()) {
      continue;
    }

    try {
      const value = storage.getItem(key);
      if (!value) continue;

      const parsed = JSON.parse(value);
      const user = extractSessionUser(parsed);
      if (user) {
        return user;
      }
    } catch (error) {
      console.warn('[publicSessionCache] Could not parse stored auth payload:', key, error);
    }
  }

  return null;
};

export const readStoredSessionUser = () => {
  try {
    if (isSignedOutMarkerActive()) return null;
    return readAuthRecord(localStorage) || readAuthRecord(sessionStorage);
  } catch (error) {
    console.warn('[publicSessionCache] Could not read stored session user:', error);
    return null;
  }
};

export const readCachedProfile = (userId) => {
  if (!userId) return null;

  try {
    return readCachedProfileEnvelope(userId)?.profile || null;
  } catch (error) {
    console.warn('[publicSessionCache] Could not read cached profile:', error);
    return null;
  }
};

export const readCachedProfileEnvelope = (userId) => {
  if (!userId) return null;

  try {
    const cachedProfile = sessionStorage.getItem(`${VERIFIED_PROFILE_STORAGE_PREFIX}${userId}`);
    if (!cachedProfile) return null;

    const parsed = JSON.parse(cachedProfile);

    if (parsed?.profile && typeof parsed.profile === 'object') {
      const cachedUserId = String(parsed.userId || parsed.profile.id || '').trim();
      if (cachedUserId && cachedUserId !== userId) return null;

      return {
        userId: cachedUserId || userId,
        profile: parsed.profile,
        verifiedAt: parsed.verifiedAt || null,
        source: parsed.source || 'verified',
      };
    }

    if (parsed?.id && typeof parsed === 'object') {
      if (String(parsed.id) !== userId) return null;

      return {
        userId,
        profile: parsed,
        verifiedAt: null,
        source: 'legacy_raw',
      };
    }
  } catch (error) {
    console.warn('[publicSessionCache] Could not parse cached profile:', error);
  }

  return null;
};

export const writeCachedProfile = (userId, profile, { source = 'verified' } = {}) => {
  if (!userId || !profile) return null;

  const envelope = {
    userId,
    profile,
    verifiedAt: Date.now(),
    source,
  };

  try {
    sessionStorage.setItem(
      `${VERIFIED_PROFILE_STORAGE_PREFIX}${userId}`,
      JSON.stringify(envelope),
    );
  } catch (error) {
    console.warn('[publicSessionCache] Could not persist cached profile:', error);
  }

  notifyVerifiedProfileChange(userId);
  return envelope;
};

export const clearCachedProfile = (userId, { notify = true } = {}) => {
  if (!userId) return;

  try {
    sessionStorage.removeItem(`${VERIFIED_PROFILE_STORAGE_PREFIX}${userId}`);
  } catch (error) {
    console.warn('[publicSessionCache] Could not clear cached profile:', error);
  }

  if (notify) {
    notifyVerifiedProfileChange(userId);
  }
};

export const clearAllCachedProfiles = () => {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(VERIFIED_PROFILE_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('[publicSessionCache] Could not clear cached profiles:', error);
  }

  notifyVerifiedProfileChange(null);
};

export const notifyVerifiedProfileChange = (userId) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(VERIFIED_PROFILE_EVENT, {
      detail: { userId: userId || null },
    }),
  );
};
